import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { api, useAuth } from '../App';
import { useCurrency } from '../contexts/CurrencyContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useLiveMarket, pollPurchase } from '../components/sblive/useLiveMarket';
import { RiskNoticeDialog } from '../components/sblive/RiskNoticeDialog';
import { WalletCard } from '../components/sblive/WalletCard';
import { HoldingsList } from '../components/sblive/HoldingsList';
import { OwnerEarningsCard } from '../components/sblive/OwnerEarningsCard';
import { FundingProgress } from '../components/sblive/FundingProgress';
import { Store, MapPin, Users, TrendingUp, Search, DollarSign, Star, Globe, ExternalLink, Share2, Twitter, Facebook, Linkedin, Link2, MessageCircle, Gift, Copy, CheckCircle, Bot, Activity, BarChart2, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { LiveTradingChart, LivePricesBoard, PortfolioPositions } from '../components/LiveTradingComponents';

export default function BusinessMarketplace() {
  const { user, updateUser } = useAuth();
  const { formatCurrency, preferredCurrency, getSymbol } = useCurrency();
  const [searchParams, setSearchParams] = useSearchParams();
  const [businesses, setBusinesses] = useState([]);
  const [myInvestments, setMyInvestments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [investAmount, setInvestAmount] = useState('');
  const [investing, setInvesting] = useState(false);
  const [showReferralPopup, setShowReferralPopup] = useState(false);
  const [sharedBusiness, setSharedBusiness] = useState(null);
  const [copiedCode, setCopiedCode] = useState(false);
  const [activeTab, setActiveTab] = useState('market');
  const [aiEnabled, setAiEnabled] = useState(false);
  const [selectedChartBusiness, setSelectedChartBusiness] = useState(null);
  const [livePrices, setLivePrices] = useState([]);
  const live = useLiveMarket();
  const [showRisk, setShowRisk] = useState(false);

  useEffect(() => {
    const sbSession = searchParams.get('sb_session');
    if (sbSession) {
      setSearchParams({});
      setActiveTab('live');
      toast.info('Confirming your card payment…');
      pollPurchase(sbSession).then((o) => {
        if (o.status === 'completed') toast.success(`Payment confirmed! You now own shares in ${o.business_name}.`);
        else toast.info('Payment is still processing. Your shares will appear shortly.');
        live.refresh();
      }).catch(() => toast.error('Could not confirm the payment yet. Please refresh in a minute.'));
    } else if (searchParams.get('sb_cancelled')) {
      setSearchParams({});
      toast.info('Payment cancelled. No charges were made.');
    } else if (searchParams.get('bank')) {
      setSearchParams({});
      setActiveTab('live');
      live.refresh();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Handle PayPal return URL parameters
  useEffect(() => {
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    const cancelled = searchParams.get('cancelled');
    const amount = searchParams.get('amount');
    
    if (success === 'true' && amount) {
      toast.success(`🎉 Investment successful! $${parseFloat(amount).toFixed(2)} invested via PayPal.`);
      // Refresh user data
      api.get('/auth/me').then(res => updateUser(res.data)).catch(() => {});
      // Clear URL params
      setSearchParams({});
    } else if (error) {
      toast.error(`Investment failed: ${error.replace(/_/g, ' ')}`);
      setSearchParams({});
    } else if (cancelled === 'true') {
      toast.info('Payment cancelled. No charges were made.');
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, updateUser]);

  // Fetch AI settings
  useEffect(() => {
    const fetchAISettings = async () => {
      try {
        const res = await api.get('/sb-market/ai-settings');
        setAiEnabled(res.data.enabled);
      } catch (error) {
        console.error('Failed to fetch AI settings:', error);
      }
    };
    fetchAISettings();
  }, []);

  // Fetch live prices
  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const res = await api.get('/sb-market/prices');
        setLivePrices(res.data);
      } catch (error) {
        console.error('Failed to fetch prices:', error);
      }
    };
    fetchPrices();
    const interval = setInterval(fetchPrices, 10000);
    return () => clearInterval(interval);
  }, []);

  // Toggle AI Trading
  const toggleAI = async (enabled) => {
    try {
      await api.post('/sb-market/ai-settings', { enabled });
      setAiEnabled(enabled);
      toast.success(enabled ? 'AI Auto-Trading Enabled! 🤖' : 'AI Auto-Trading Disabled');
    } catch (error) {
      toast.error('Failed to update AI settings');
    }
  };

  // Get live price for a business
  const getLivePrice = (businessId) => {
    const livePrice = livePrices.find(p => p.business_id === businessId);
    return livePrice?.price || null;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [bizRes, invRes] = await Promise.all([
          api.get('/business/listings'),
          api.get('/business/investments')
        ]);
        setBusinesses(bizRes.data);
        setMyInvestments(invRes.data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatCompact = (value) => {
    return formatCurrency(value, { compact: true });
  };

  const formatNetWorth = (value) => {
    if (value >= 1000000000) {
      return `$${(value / 1000000000).toFixed(0)}B`;
    }
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(0)}M`;
    }
    return `$${(value / 1000).toFixed(0)}K`;
  };

  const handleInvest = async () => {
    const amount = parseFloat(investAmount);
    if (!amount || amount < 5) return toast.error('Minimum investment is $5');
    if (amount > 1000) return toast.error('Maximum investment is $1,000');
    if (!live.status?.risk_accepted) return setShowRisk(true);
    setInvesting(true);
    try {
      await live.startPurchase(selectedBusiness.business_id, amount);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Card payment could not start');
      setInvesting(false);
    }
  };

  const filteredBusinesses = searchQuery
    ? businesses.filter(b => 
        b.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        b.category.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : businesses;

  // Share business function
  const handleShare = (business, platform, e) => {
    e.stopPropagation();
    const baseUrl = window.location.origin;
    const referralCode = user?.referral_code || '';
    const shareUrl = referralCode 
      ? `${baseUrl}/register?ref=${referralCode}&invest=${business.business_id}`
      : `${baseUrl}/business?invest=${business.business_id}`;
    const shareText = `Check out ${business.name} on Sraivest! ${String(business.expected_return).replace(/%/g, '')}% target return (not guaranteed). Invest starting from just $5.`;
    
    const shareLinks = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`,
    };
    
    if (platform === 'copy') {
      navigator.clipboard.writeText(shareUrl).then(() => {
        toast.success('Link copied to clipboard!');
      }).catch(() => {
        toast.success('Share link: ' + shareUrl);
      });
    } else {
      window.open(shareLinks[platform], '_blank', 'width=600,height=400');
    }
    
    // Show referral bonus popup after sharing
    setSharedBusiness(business);
    setTimeout(() => setShowReferralPopup(true), 500);
  };

  // Copy referral code function
  const copyReferralCode = () => {
    const code = user?.referral_code || '';
    navigator.clipboard.writeText(code).then(() => {
      setCopiedCode(true);
      toast.success('Referral code copied!');
      setTimeout(() => setCopiedCode(false), 2000);
    }).catch(() => {
      toast.info(`Your referral code: ${code}`);
    });
  };

  // Copy referral link function
  const copyReferralLink = () => {
    const baseUrl = window.location.origin;
    const referralCode = user?.referral_code || '';
    const link = `${baseUrl}/register?ref=${referralCode}`;
    navigator.clipboard.writeText(link).then(() => {
      toast.success('Referral link copied!');
    }).catch(() => {
      toast.info(`Your referral link: ${link}`);
    });
  };

  // Trade History Component
  const TradeHistory = () => {
    const [trades, setTrades] = useState([]);
    const [loadingTrades, setLoadingTrades] = useState(true);

    useEffect(() => {
      const fetchTrades = async () => {
        try {
          const res = await api.get('/sb-market/trade-history');
          setTrades(res.data);
        } catch (error) {
          console.error('Failed to fetch trades:', error);
        } finally {
          setLoadingTrades(false);
        }
      };
      fetchTrades();
    }, []);

    if (loadingTrades) return <div className="animate-pulse h-20 bg-white/5 rounded-lg" />;
    if (trades.length === 0) return <p className="text-gray-500 text-center py-4">No trades yet</p>;

    return (
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {trades.slice(0, 10).map((trade, i) => (
          <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                trade.action === 'BUY' ? 'bg-[#00E676]/20' : 'bg-[#F7931A]/20'
              }`}>
                <span className={`text-xs font-bold ${trade.action === 'BUY' ? 'text-[#00E676]' : 'text-[#F7931A]'}`}>
                  {trade.action === 'BUY' ? 'B' : 'S'}
                </span>
              </div>
              <div>
                <p className="text-sm text-white">{trade.business_name}</p>
                <p className="text-xs text-gray-500">
                  {trade.shares?.toFixed(4)} shares @ ${trade.price?.toFixed(2)}
                  {trade.ai_trade && <span className="ml-2 text-[#6366F1]">🤖 AI</span>}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold text-white">${trade.amount?.toFixed(2)}</p>
              {trade.profit !== undefined && trade.action === 'SELL' && (
                <p className={`text-xs ${trade.profit >= 0 ? 'text-[#00E676]' : 'text-red-500'}`}>
                  {trade.profit >= 0 ? '+' : ''}${trade.profit?.toFixed(2)}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Share Button Component
  const ShareButton = ({ business }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-8 w-8 p-0 hover:bg-white/10"
          onClick={(e) => e.stopPropagation()}
          data-testid={`share-btn-${business.business_id}`}
        >
          <Share2 className="w-4 h-4 text-gray-400 hover:text-white" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#1A1A1A] border-white/10">
        <DropdownMenuItem 
          onClick={(e) => handleShare(business, 'twitter', e)}
          className="cursor-pointer hover:bg-white/10 text-gray-300"
        >
          <Twitter className="w-4 h-4 mr-2 text-[#1DA1F2]" /> Share on X
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={(e) => handleShare(business, 'facebook', e)}
          className="cursor-pointer hover:bg-white/10 text-gray-300"
        >
          <Facebook className="w-4 h-4 mr-2 text-[#4267B2]" /> Share on Facebook
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={(e) => handleShare(business, 'linkedin', e)}
          className="cursor-pointer hover:bg-white/10 text-gray-300"
        >
          <Linkedin className="w-4 h-4 mr-2 text-[#0077B5]" /> Share on LinkedIn
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={(e) => handleShare(business, 'whatsapp', e)}
          className="cursor-pointer hover:bg-white/10 text-gray-300"
        >
          <MessageCircle className="w-4 h-4 mr-2 text-[#25D366]" /> Share on WhatsApp
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={(e) => handleShare(business, 'copy', e)}
          className="cursor-pointer hover:bg-white/10 text-gray-300"
        >
          <Link2 className="w-4 h-4 mr-2 text-[#00E676]" /> Copy Link
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  // Separate featured (Sparkx) from regular businesses
  const featuredBusiness = filteredBusinesses.find(b => b.featured);
  const regularBusinesses = filteredBusinesses.filter(b => !b.featured);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-2 border-[#00E676] border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="business-page">
        {/* Header with LIVE badge */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Small Business Market
              </h1>
              <span className="px-3 py-1 text-sm rounded-full bg-[#00E676] text-black font-semibold animate-pulse">
                LIVE TRADING
              </span>
            </div>
            <p className="text-gray-400 mt-1">Real money • Pay by card • Start with $5</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input
                placeholder="Search businesses..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input-dark pl-10 w-48"
              />
            </div>
          </div>
        </div>

        <div className="p-4 rounded-xl border bg-[#F7931A]/5 border-[#F7931A]/30 text-sm text-gray-300" data-testid="live-market-notice">
          Real money: purchases are paid by card through Stripe. Share prices are fixed at the listed price. AI auto-trading is turned off. Investing in small businesses is risky and you can lose your money.
        </div>

        {/* Tabs: Market, Portfolio, Chart */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-white/5 border border-white/10">
            <TabsTrigger value="market" className="data-[state=active]:bg-[#00E676] data-[state=active]:text-black">
              <Store className="w-4 h-4 mr-2" /> Market
            </TabsTrigger>
            <TabsTrigger value="portfolio" className="data-[state=active]:bg-[#00E676] data-[state=active]:text-black">
              <Wallet className="w-4 h-4 mr-2" /> Practice Portfolio
            </TabsTrigger>
            <TabsTrigger value="live" className="data-[state=active]:bg-[#00E676] data-[state=active]:text-black" data-testid="live-tab">
              <DollarSign className="w-4 h-4 mr-2" /> My Shares & Cash
            </TabsTrigger>
            <TabsTrigger value="chart" className="data-[state=active]:bg-[#00E676] data-[state=active]:text-black">
              <BarChart2 className="w-4 h-4 mr-2" /> Live Charts
            </TabsTrigger>
          </TabsList>

          {/* Market Tab */}
          <TabsContent value="market" className="mt-6">
            {/* Live Prices Ticker */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                <Activity className="w-5 h-5 text-[#00E676]" />
                Live Prices
                <span className="w-2 h-2 rounded-full bg-[#00E676] animate-pulse" />
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                {livePrices.slice(0, 6).map((price) => {
                  const isUp = price.change_pct >= 0;
                  return (
                    <div 
                      key={price.business_id}
                      className={`p-2 rounded-lg cursor-pointer transition-all hover:scale-105 ${
                        isUp ? 'bg-[#00E676]/10 border border-[#00E676]/20' : 'bg-red-500/10 border border-red-500/20'
                      }`}
                      onClick={() => {
                        setSelectedChartBusiness(price);
                        setActiveTab('chart');
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <img src={price.logo_url} alt={price.name} className="w-8 h-8 rounded object-cover" />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs text-white truncate">{price.symbol}</p>
                          <p className="text-sm font-bold text-white">${price.price?.toFixed(2)}</p>
                          <p className={`text-xs ${isUp ? 'text-[#00E676]' : 'text-red-500'}`}>
                            {isUp ? '+' : ''}{price.change_pct?.toFixed(2)}%
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Featured Business */}
        {featuredBusiness && (
          <Card 
            className="card-dashboard border-2 border-[#6366F1] overflow-hidden cursor-pointer hover:border-[#6366F1]/80 transition-all"
            onClick={() => setSelectedBusiness(featuredBusiness)}
            data-testid="featured-business"
          >
            <div className="relative">
              <div className="absolute top-4 left-4 z-10 flex items-center gap-2">
                <span className="px-3 py-1 rounded-full bg-[#6366F1] text-white text-sm font-medium flex items-center gap-1">
                  <Star className="w-4 h-4" /> Featured Investment
                </span>
              </div>
              <div className="absolute top-4 right-4 z-10">
                <ShareButton business={featuredBusiness} />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="p-8 flex flex-col justify-center">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-16 h-16 rounded-xl bg-white/10 flex items-center justify-center overflow-hidden">
                      <img 
                        src={featuredBusiness.logo_url || featuredBusiness.image_url} 
                        alt={featuredBusiness.name}
                        className="w-full h-full object-contain"
                        onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=200'; }}
                      />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {featuredBusiness.name}
                      </h2>
                      <span className="text-[#6366F1]">{featuredBusiness.category}</span>
                    </div>
                  </div>
                  
                  <p className="text-gray-300 mb-6">{featuredBusiness.description}</p>
                  
                  <div className="flex items-center gap-4 text-sm text-gray-400 mb-6">
                    {featuredBusiness.location && <span className="flex items-center gap-1"><Globe className="w-4 h-4" />{featuredBusiness.location}</span>}
                    {featuredBusiness.website && (
                      <a 
                        href={featuredBusiness.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-[#6366F1] hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="w-4 h-4" /> Visit Website
                      </a>
                    )}
                  </div>

                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="p-3 rounded-lg bg-white/5">
                      <p className="text-xs text-gray-500">Goal</p>
                      <p className="text-lg font-bold text-white">{formatCurrency(featuredBusiness.goal || featuredBusiness.investment_goal)}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-white/5">
                      <p className="text-xs text-gray-500">Min Investment</p>
                      <p className="text-lg font-bold text-[#00E676]">{formatCurrency(featuredBusiness.min_investment)}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-white/5">
                      <p className="text-xs text-gray-500">Target (not guaranteed)</p>
                      <p className="text-lg font-bold text-[#F7931A]">{String(featuredBusiness.expected_return).replace(/%/g, '')}%</p>
                    </div>
                  </div>

                  <div className="mb-4">
                    <FundingProgress biz={featuredBusiness} formatCurrency={formatCurrency} large />
                  </div>

                  {/* 5-Year Net Worth Evolution for Featured */}
                  {featuredBusiness.current_value && featuredBusiness.projected_value && (
                    <div className="mb-4 p-4 rounded-lg bg-gradient-to-r from-[#00E676]/10 to-[#6366F1]/10 border border-[#00E676]/20">
                      <p className="text-sm text-gray-400 mb-2">5-Year Estimated Net Worth Evolution</p>
                      <div className="flex items-center gap-3">
                        <span className="text-2xl font-bold text-white">{formatNetWorth(featuredBusiness.current_value)}</span>
                        <TrendingUp className="w-6 h-6 text-[#00E676]" />
                        <span className="text-2xl font-bold text-[#00E676]">{formatNetWorth(featuredBusiness.projected_value)}</span>
                      </div>
                    </div>
                  )}

                  <Button className="btn-primary w-full" data-testid="invest-sparkx-btn">
                    <TrendingUp className="w-5 h-5 mr-2" />
                    Invest in {featuredBusiness.name}
                  </Button>
                </div>
                
                <div className="hidden lg:block relative bg-gradient-to-br from-[#6366F1]/20 to-[#6366F1]/5">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <p className="text-6xl font-bold text-[#6366F1]">{String(featuredBusiness.expected_return).replace(/%/g, '')}%</p>
                      <p className="text-xl text-gray-400">Target return (not guaranteed)</p>
                      <p className="text-sm text-gray-500 mt-4">Start with just ${featuredBusiness.min_investment}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Regular Business Listings */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {regularBusinesses.map((biz) => (
            <Card 
              key={biz.business_id} 
              className="card-dashboard overflow-hidden hover:border-white/20 transition-colors cursor-pointer group"
              onClick={() => setSelectedBusiness(biz)}
              data-testid={`business-${biz.business_id}`}
            >
              <div className="h-40 overflow-hidden relative">
                <img src={biz.logo_url || biz.image_url} alt={biz.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute top-3 right-3 flex items-center gap-2">
                  <ShareButton business={biz} />
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    biz.status === 'active' ? 'bg-[#00E676]/20 text-[#00E676]' :
                    biz.status === 'funded' ? 'bg-[#6366F1]/20 text-[#6366F1]' :
                    'bg-gray-500/20 text-gray-400'
                  }`}>
                    {biz.status}
                  </span>
                </div>
              </div>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-0.5 rounded bg-white/10 text-xs text-gray-400">{biz.category}</span>
                </div>
                <h3 className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{biz.name}</h3>
                <p className="text-sm text-gray-400 mt-1 line-clamp-2">{biz.description}</p>
                
                <div className="flex items-center gap-4 mt-4 text-sm text-gray-500">
                  {biz.location && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{biz.location}</span>}
                </div>

                <div className="mt-4">
                  <FundingProgress biz={biz} formatCurrency={formatCurrency} />
                </div>

                <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
                  <div>
                    <p className="text-xs text-gray-500">Share Price</p>
                    <p className="font-semibold text-white number">{formatCurrency(biz.share_price || biz.min_investment)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Target (not guaranteed)</p>
                    <p className="font-semibold text-[#00E676]">{String(biz.expected_return).replace(/%/g, '')}%</p>
                  </div>
                </div>
                <p className="text-xs text-[#F7931A] mt-2">Min $5 • Fractional shares</p>

                {/* 5-Year Net Worth Evolution */}
                {biz.current_value && biz.projected_value && (
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <p className="text-xs text-gray-500 mb-2">5-Year Est. Net Worth</p>
                    <div className="flex items-center gap-2">
                      <span className="text-white font-semibold">{formatNetWorth(biz.current_value)}</span>
                      <TrendingUp className="w-4 h-4 text-[#00E676]" />
                      <span className="text-[#00E676] font-semibold">{formatNetWorth(biz.projected_value)}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
          </TabsContent>

          {/* Portfolio Tab */}
          <TabsContent value="live" className="mt-6 space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <WalletCard status={live.status} onChange={live.refresh} linkBank={live.linkBank} />
              <OwnerEarningsCard owner={live.status?.owner_business} bank={live.status?.bank} linkBank={live.linkBank} />
            </div>
            <HoldingsList portfolio={live.portfolio} onChange={live.refresh} />
          </TabsContent>

          <TabsContent value="portfolio" className="mt-6">
            <div className="space-y-6">
              <Card className="card-dashboard">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    <Wallet className="w-5 h-5 text-[#00E676]" />
                    My Small Business Portfolio
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <PortfolioPositions />
                </CardContent>
              </Card>
              
              {/* Trade History */}
              <Card className="card-dashboard">
                <CardHeader>
                  <CardTitle className="text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Recent Trades</CardTitle>
                </CardHeader>
                <CardContent>
                  <TradeHistory />
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Charts Tab */}
          <TabsContent value="chart" className="mt-6">
            <div className="space-y-6">
              {/* Chart Selection */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {livePrices.map((price) => (
                  <Button
                    key={price.business_id}
                    variant={selectedChartBusiness?.business_id === price.business_id ? 'default' : 'outline'}
                    className={selectedChartBusiness?.business_id === price.business_id 
                      ? 'bg-[#00E676] text-black hover:bg-[#00E676]/80' 
                      : 'border-white/10'
                    }
                    onClick={() => setSelectedChartBusiness(price)}
                  >
                    <img src={price.logo_url} alt={price.name} className="w-5 h-5 rounded mr-2 object-cover" />
                    {price.symbol}
                  </Button>
                ))}
              </div>
              
              {/* Live Chart */}
              {selectedChartBusiness ? (
                <Card className="card-dashboard">
                  <CardContent className="pt-6">
                    <LiveTradingChart 
                      businessId={selectedChartBusiness.business_id}
                      name={selectedChartBusiness.name}
                      height={400}
                    />
                  </CardContent>
                </Card>
              ) : (
                <Card className="card-dashboard">
                  <CardContent className="py-12 text-center">
                    <BarChart2 className="w-12 h-12 mx-auto mb-4 text-gray-500" />
                    <p className="text-gray-400">Select a business above to view its live chart</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>

        <RiskNoticeDialog open={showRisk} onClose={() => setShowRisk(false)}
          onAccept={async () => { await live.acceptRisk(); setShowRisk(false); toast.success('Thanks! You can now pay by card.'); }} />

        {/* Invest Dialog - Compact Version */}
        <Dialog open={!!selectedBusiness} onOpenChange={() => setSelectedBusiness(null)}>
          <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-md max-h-[85vh] overflow-y-auto" data-sb-live>
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Invest in {selectedBusiness?.name}</DialogTitle>
                {selectedBusiness && <ShareButton business={selectedBusiness} />}
              </div>
            </DialogHeader>

            <div className="space-y-3 py-2">
              {/* Compact header with logo */}
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                  <img 
                    src={selectedBusiness?.logo_url || selectedBusiness?.image_url} 
                    alt={selectedBusiness?.name}
                    className="w-full h-full object-contain"
                    onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=200'; }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-400 line-clamp-2">{selectedBusiness?.description}</p>
                </div>
              </div>
              
              {/* Compact stats row */}
              <div className="grid grid-cols-3 gap-2">
                <div className="p-2 rounded-lg bg-white/5 text-center">
                  <p className="text-xs text-gray-500">Share Price</p>
                  <p className="font-semibold text-white text-sm">{formatCurrency(selectedBusiness?.share_price)}</p>
                </div>
                <div className="p-2 rounded-lg bg-white/5 text-center">
                  <p className="text-xs text-gray-500">Target (not guaranteed)</p>
                  <p className="font-semibold text-[#00E676] text-sm">{String(selectedBusiness?.expected_return ?? '').replace(/%/g, '')}%</p>
                </div>
                <div className="p-2 rounded-lg bg-white/5 text-center">
                  <p className="text-xs text-gray-500">Min</p>
                  <p className="font-semibold text-[#F7931A] text-sm">$5</p>
                </div>
              </div>

              {/* 5-Year Net Worth - Compact */}
              {selectedBusiness?.current_value && selectedBusiness?.projected_value && (
                <div className="flex items-center justify-between p-2 rounded-lg bg-gradient-to-r from-[#00E676]/10 to-[#6366F1]/10 border border-[#00E676]/20">
                  <span className="text-xs text-gray-400">5Y Net Worth</span>
                  <div className="flex items-center gap-1">
                    <span className="font-semibold text-white text-sm">{formatNetWorth(selectedBusiness.current_value)}</span>
                    <TrendingUp className="w-3 h-3 text-[#00E676]" />
                    <span className="font-semibold text-[#00E676] text-sm">{formatNetWorth(selectedBusiness.projected_value)}</span>
                  </div>
                </div>
              )}

              {/* Investment Input */}
              <div className="space-y-2">
                <label className="text-sm text-gray-400">Investment Amount</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    type="number"
                    value={investAmount}
                    onChange={(e) => setInvestAmount(e.target.value)}
                    placeholder={`Min $5`}
                    className="input-dark pl-10"
                    min="5"
                    max="1000"
                    data-testid="invest-amount-input"
                  />
                </div>
                {/* Preset Investment Amounts */}
                <div className="grid grid-cols-4 gap-2">
                  {[5, 15, 25, 50, 100, 250, 500, 1000].map((preset) => (
                    <Button
                      key={preset}
                      variant="outline"
                      size="sm"
                      className={`border-white/10 text-xs ${investAmount === String(preset) ? 'bg-[#6366F1] text-white border-[#6366F1]' : ''}`}
                      onClick={() => setInvestAmount(String(preset))}
                      data-testid={`invest-preset-${preset}`}
                    >
                      ${preset >= 1000 ? '1k' : preset}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Real-time Share Calculation - Compact */}
              {investAmount && selectedBusiness?.share_price && (
                <div className="p-3 rounded-lg bg-[#6366F1]/10 border border-[#6366F1]/30">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-400">Shares You'll Own</span>
                    <span className="text-[#00E676] font-bold">
                      {((parseFloat(investAmount) || 0) / selectedBusiness.share_price).toFixed(4)} shares
                    </span>
                  </div>
                  {(parseFloat(investAmount) || 0) < selectedBusiness.share_price && (
                    <p className="text-xs text-[#F7931A] mt-1">
                      💡 Fractional share ({(((parseFloat(investAmount) || 0) / selectedBusiness.share_price) * 100).toFixed(1)}% of one share)
                    </p>
                  )}
                </div>
              )}

              {live.status && !live.status.card_payments_enabled ? (
                <p className="p-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-xs" data-testid="card-payments-unavailable">
                  Card payments are temporarily unavailable. Please check back soon.
                </p>
              ) : (
                <p className="text-xs text-gray-500">You'll pay securely by card on Stripe. 5% comes back to your market wallet as cashback.</p>
              )}
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setSelectedBusiness(null)} className="btn-secondary">Cancel</Button>
              <Button onClick={handleInvest} disabled={investing || selectedBusiness?.status !== 'active' || !investAmount || parseFloat(investAmount) < 5} className="btn-primary" data-testid="confirm-invest-btn">
                {investing ? <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" /> : 
                  'Pay by card'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Referral Bonus Popup */}
        <Dialog open={showReferralPopup} onOpenChange={setShowReferralPopup}>
          <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <Gift className="w-6 h-6 text-[#F7931A]" />
                Earn Rewards by Sharing!
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Reward Amount Highlight */}
              <div className="text-center p-6 rounded-2xl bg-gradient-to-br from-[#F7931A]/20 via-[#00E676]/10 to-[#6366F1]/20 border border-[#F7931A]/30">
                <p className="text-gray-400 mb-2">Earn up to</p>
                <p className="text-5xl font-bold text-[#F7931A]">$10</p>
                <p className="text-gray-400 mt-2">for each friend who signs up & invests</p>
              </div>

              {/* How it works */}
              <div className="space-y-3">
                <h4 className="font-semibold text-white">How it works:</h4>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#00E676]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-[#00E676] text-sm font-bold">1</span>
                  </div>
                  <p className="text-gray-400 text-sm">Share {sharedBusiness?.name || 'a business'} with your friends using your unique link</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#6366F1]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-[#6366F1] text-sm font-bold">2</span>
                  </div>
                  <p className="text-gray-400 text-sm">Your friend signs up using your referral code</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#F7931A]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-[#F7931A] text-sm font-bold">3</span>
                  </div>
                  <p className="text-gray-400 text-sm">When they make their first investment, you both get <span className="text-[#00E676] font-semibold">$10 bonus!</span></p>
                </div>
              </div>

              {/* Referral Code */}
              <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                <p className="text-xs text-gray-500 mb-2">Your Referral Code</p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 px-4 py-3 rounded-lg bg-[#0D0D0D] border border-white/10 font-mono text-lg text-[#00E676] tracking-wider">
                    {user?.referral_code || 'Loading...'}
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={copyReferralCode}
                    className="h-12 px-4 border-[#00E676]/30 hover:bg-[#00E676]/10"
                    data-testid="copy-referral-code-btn"
                  >
                    {copiedCode ? <CheckCircle className="w-5 h-5 text-[#00E676]" /> : <Copy className="w-5 h-5 text-gray-400" />}
                  </Button>
                </div>
              </div>

              {/* Quick Share Again */}
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="flex-1 border-[#1DA1F2]/30 hover:bg-[#1DA1F2]/10"
                  onClick={() => {
                    const baseUrl = window.location.origin;
                    const link = `${baseUrl}/register?ref=${user?.referral_code}`;
                    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Join me on Sraivest and start investing with just $5! Use my referral code: ${user?.referral_code}`)}&url=${encodeURIComponent(link)}`, '_blank', 'width=600,height=400');
                  }}
                >
                  <Twitter className="w-4 h-4 mr-2 text-[#1DA1F2]" /> Share Again
                </Button>
                <Button 
                  variant="outline" 
                  className="flex-1 border-[#25D366]/30 hover:bg-[#25D366]/10"
                  onClick={() => {
                    const baseUrl = window.location.origin;
                    const link = `${baseUrl}/register?ref=${user?.referral_code}`;
                    window.open(`https://wa.me/?text=${encodeURIComponent(`Join me on Sraivest and start investing with just $5! Use my referral code: ${user?.referral_code} ${link}`)}`, '_blank', 'width=600,height=400');
                  }}
                >
                  <MessageCircle className="w-4 h-4 mr-2 text-[#25D366]" /> WhatsApp
                </Button>
              </div>

              {/* Copy Full Link */}
              <Button 
                className="w-full btn-primary"
                onClick={copyReferralLink}
                data-testid="copy-referral-link-btn"
              >
                <Link2 className="w-4 h-4 mr-2" /> Copy My Referral Link
              </Button>
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowReferralPopup(false)} className="text-gray-400 hover:text-white">
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
